/**
 * Telegram Mini App initialisation — the only module in the app that touches the
 * platform. ADR-0006.
 *
 * Import this only after `../launch`: `init()` reloads the page and strips the launch
 * params out of the URL.
 *
 * Ported from `legacy/frontend/src/telegram.ts` at legacy commit 2953e3f. The one
 * change is FND-004: the three version-gated calls go through `./gate` instead of
 * carrying their own `?.`/`try`/`catch`. Same degradations, expressed once.
 */

import {
  backButton,
  init,
  initDataRaw,
  requestWriteAccess,
  restoreInitData,
} from '@telegram-apps/sdk';

import { isHandheld, webApp } from './bridge';
import { attempt } from './gate';

export { clientVersion, platform, HANDHELD, isHandheld } from './bridge';
export { atLeast, attempt, CAPABILITIES, supports } from './gate';

export function initTelegram(): void {
  try {
    init();
  } catch {
    // The app is open outside Telegram — in a browser during development, say.
  }
  // A separate attempt rather than a continuation of the previous one: without this
  // call initDataRaw() is always empty, the initData signals are populated only here.
  // Folding it into the same try as init() means losing the login data every time
  // init() trips over something of its own — which is exactly how login used to break.
  try {
    restoreInitData();
  } catch {
    // No launch data: the app was not opened through a Telegram link.
  }
  const bridge = webApp();
  bridge?.ready?.();
  bridge?.expand?.();
  if (bridge !== undefined && isHandheld()) {
    fillScreen();
    keepUpright();
    // Leaving is the «✕» and nothing else. A drag down the page closed the app whenever
    // the content happened to be at its top, and on this app that is most of the time:
    // the panel is one screen, and the regent is holding the phone out at arm's length
    // while the choir arrives.
    attempt('disableVerticalSwipes');
  }
}

/**
 * The whole screen, Telegram's own header included.
 *
 * What it buys is the strip that header occupied — a screen title's worth of a phone.
 * What it costs is that the client's close and «…» buttons then float over our top-right
 * corner, and the notch over everything. Both are measured for us as
 * `--tg-safe-area-inset-top` and `--tg-content-safe-area-inset-top`, and the shell's top
 * padding is the sum: without spending it the choir's name sits under the buttons, which
 * is the state this call would otherwise leave the app in.
 */
function fillScreen(): void {
  attempt('requestFullscreen');
}

/**
 * Upright, and it stays upright.
 *
 * Sideways the app is a 32rem column in the middle of an empty screen with the tab bar a
 * hand's width from what it belongs to, and the code plate — the one thing meant to be
 * seen across a loft — loses most of its height.
 *
 * Telegram locks whichever way the phone is being held at the moment of the call, so
 * calling it on a phone that is already sideways would freeze the app in exactly the
 * shape this is here to prevent. Hence the wait for the first upright moment instead.
 */
function keepUpright(): void {
  const lockIfUpright = (): boolean => {
    if (window.innerWidth > window.innerHeight) return false;
    return attempt('lockOrientation');
  };
  if (lockIfUpright()) return;
  const onResize = () => {
    if (lockIfUpright()) window.removeEventListener('resize', onResize);
  };
  window.addEventListener('resize', onResize);
}

/* The light and dark sets used to be resolved here, with the client's theme winning over
   the system setting. Nothing resolves them any more: the palette is ours
   (legacy/DESIGN.md, 2026-08-13), so a Telegram theme says nothing about the room the
   phone is in, and index.css follows nothing. One theme, and it is dark. FND-003. */

/**
 * Raw initData, to be exchanged for our own JWT. Verified on the server.
 *
 * Two sources not out of distrust for the SDK but because of the cost of failure: the
 * SDK's parsing is strict (the `signature` field, for one, is mandatory), and a single
 * unrecognised field leaves the app with no login at all — showing "open it from
 * Telegram" inside Telegram. The official `window.Telegram.WebApp` hands over the same
 * string unparsed, and the server verifies it either way, so the fallback weakens
 * nothing. FND-001 (`KEEP`).
 */
export function getInitDataRaw(): string | null {
  try {
    const fromSdk = initDataRaw();
    if (fromSdk !== undefined && fromSdk !== '') return fromSdk;
  } catch {
    // No launch data, or the SDK could not parse it — try the official object.
  }
  const fromWebApp = webApp()?.initData;
  return fromWebApp !== undefined && fromWebApp !== '' ? fromWebApp : null;
}

/**
 * Whether Telegram already lets the bot write, as of this launch.
 *
 * Read out of initData rather than asked for. Somebody who followed an invite link
 * arrives through the chat with the bot — `?start=`, see `_invite_link` on the server —
 * and opening that chat is itself the permission, so the flag is already true before
 * anything on this screen has run.
 *
 * Parsed from the raw string rather than taken from the SDK's own object: the string is
 * what both paths in `getInitDataRaw` agree on, and this has to hold on a client old
 * enough not to have the second.
 */
export function writeAccessAlreadyGranted(): boolean {
  const raw = getInitDataRaw();
  if (raw === null) return false;
  try {
    const user = new URLSearchParams(raw).get('user');
    if (user === null) return false;
    return (JSON.parse(user) as { allows_write_to_pm?: boolean }).allows_write_to_pm === true;
  } catch {
    // Unreadable initData is not a permission.
    return false;
  }
}

/**
 * Ask for permission to write in DMs and return the real answer.
 *
 * The only reminder channel: the platform has no push, and asking later costs more. The
 * return value matters: treating consent as granted by default would fill the database
 * with recipients Telegram is going to refuse.
 *
 * Nothing is asked when it has already been given. Whether a client redraws the popup
 * for a permission it already holds is not written down anywhere in Telegram's
 * documentation, and the app must not depend on the answer: a popup with one possible
 * answer is a question that should not have been put.
 */
export async function askWriteAccess(): Promise<boolean> {
  if (writeAccessAlreadyGranted()) return true;
  try {
    if (!requestWriteAccess.isAvailable()) return false;
    return (await requestWriteAccess()) === 'allowed';
  } catch {
    // A refusal is not an error: the reminders simply will not arrive.
    return false;
  }
}

/**
 * Whoever is on top of the screen holds the client's back key, and gives it up again on
 * the way out.
 *
 * Two things at once on Android: Telegram's «‹» in its header, and the phone's own back
 * key, which the client routes to the same event once the button is shown. Neither
 * exists outside Telegram and neither exists on a client older than Bot API 6.1, so this
 * is silent there — a browser has a back button of its own, and the sheet has two other
 * ways out on any client at all.
 *
 * A stack and not a subscription each, because the SDK's `onClick` is `on()` from the
 * bridge — a plain emitter that calls everybody who ever registered. A sheet over a
 * person's card is two holders, and before this both ran on one press: the sheet closed and
 * the card went back to «Хор» in the same movement. Only the last holder registered is called
 * now — `holders.at(-1)`, not the innermost: legacy F-017 §7.1, `PersonScreen.backKey.test.tsx`.
 */
const holders: Array<() => void> = [];

/** The one subscription the stack is read through, while anybody holds it. */
let pressed: (() => void) | null = null;

export function onBackButton(fn: () => void): () => void {
  if (!backButton.mount.isAvailable()) return () => {};
  holders.push(fn);
  if (pressed === null) {
    backButton.mount();
    // The subscription first, because it is the half that cannot be refused: it listens,
    // where drawing the arrow is a message to the client and outside one throws
    // outright. Losing the press because the arrow could not be drawn would be giving up
    // the wrong half.
    pressed = backButton.onClick(() => holders.at(-1)?.());
    draw(true);
  }
  return () => {
    // By identity and from the top: a screen deeper than this one may still be holding
    // the key when this cleanup runs, and taking the last element regardless would take
    // that one instead.
    const at = holders.lastIndexOf(fn);
    if (at !== -1) holders.splice(at, 1);
    if (holders.length > 0) return;
    pressed?.();
    pressed = null;
    draw(false);
    backButton.unmount();
  };
}

/** Showing and hiding both post to the client, and outside one they throw. */
function draw(visible: boolean): void {
  try {
    if (visible) backButton.show();
    else backButton.hide();
  } catch {
    // Nowhere to post to — a browser tab, or the rig. The subscription is untouched by
    // it, and there is nothing drawn to put away.
  }
}

/**
 * A tap of the phone's motor for six moments, and those six are the whole list: the mark
 * landing, a fresh arrival on the regent's panel, a status change in the roster, each
 * digit of the code, a value passing under the band of a wheel, a refusal. The rule is
 * the motion rule read by the hand: haptics confirm an event, they do not decorate a
 * touch — nothing on plain button presses or tab switches.
 *
 * Outside Telegram there is no bridge and the function is silent, which is the right
 * amount of feedback for a browser tab. There is no bridge on Desktop either.
 */
export function haptic(kind: 'success' | 'error' | 'light' | 'selection'): void {
  const bridge = webApp()?.HapticFeedback;
  if (bridge === undefined) return;
  try {
    if (kind === 'success' || kind === 'error') bridge.notificationOccurred?.(kind);
    else if (kind === 'light') bridge.impactOccurred?.('light');
    else bridge.selectionChanged?.();
  } catch {
    // A client too old to know the method: the event still happened on screen.
  }
}

export const NATIVE_SCANNER_TIMEOUT_MS = 12_000;

/**
 * Telegram's native scanner. Works on mobile clients only, and when camera access is
 * denied neither an event nor an error arrives — hence our own timeout. The main path is
 * a different one anyway: the QR is a deep link, read with the system camera.
 */
export function scanQr(): Promise<string | null> {
  const bridge = webApp();
  if (bridge?.showScanQrPopup === undefined) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      try {
        bridge.closeScanQrPopup?.();
      } catch {
        // The popup may have closed on its own.
      }
      resolve(value);
    };

    const timer = window.setTimeout(() => finish(null), NATIVE_SCANNER_TIMEOUT_MS);

    try {
      bridge.showScanQrPopup?.({ text: 'Наведи на код спевки' }, (text: string) => {
        finish(text);
        return true;
      });
    } catch {
      finish(null);
    }
  });
}
